import {useEffect, useRef, useState} from 'react';
import * as d3Force from 'd3-force';
import * as d3Selection from 'd3-selection';
import * as d3Zoom from 'd3-zoom';
import * as d3Drag from 'd3-drag';
import 'd3-transition'; // Enable transition methods on selections
import type {FriendData, ProfileSummary, SteamID64} from '../types/steam';

const TRANSITION_DURATION = 300;

// Toggle to render pure leaf friend-of-friend nodes (depth 2 nodes with only 1 connection)
// Value of false: hide leaf FoF nodes for a cleaner graph
const RENDER_LEAF_FOF_NODES = false;

const distPerMutual = (mutuals: number) => {
    const mutualCount = Math.min(mutuals, 5);
    // 5+ friends = 80px, 0 friends = 200px (30px per tier difference)
    const distPerMutual = 24;
    return distPerMutual * 5 - (mutualCount * distPerMutual)
    // return distPerMutual * mutualCount;
}
const forcePerMutual = (mutuals: number) => {
    return distPerMutual(mutuals);
}

const distCalc = (d: NetworkLink) => {
    const source = d.source as NetworkNode;
    const target = d.target as NetworkNode;

    if (source.depth === 1 && target.depth == 1) {
        return 0;
    }

    if (source.depth === 2 && target.depth == 1) {
        return 0;
    }
    if (source.depth === 1 && target.depth == 2) {
        return -80;
    }

    if (source.isCenter) {
        // Distance from root based on mutual friends (capped at 5)
        // More visible differences: 0 mutual = far, 5+ mutual = close
        return distPerMutual(target.mutualFriendsCount);
    }
    // FoF to Friend: much tighter clustering
    return -80;
}

const forceCalc = (d: NetworkNode) => {
    if (d.depth === 0) return 0;
    if (d.depth === 1) {
        // Depth-1 nodes: radial distance based on mutual friends
        return 4 * forcePerMutual(d.mutualFriendsCount);
    }
    // Depth-2 nodes: stay close to their friend (not to root)
    return 280;
}

interface NetworkNode extends d3Force.SimulationNodeDatum {
    id: SteamID64;
    name: string;
    vacBanned: boolean;
    gameBans: number;
    friendBanCount: number;
    isCenter: boolean;
    depth: number; // 0 = root, 1 = friend, 2 = friend of friend
    mutualFriendsCount: number; // number of mutual friends with root
    totalFriendCount: number; // total number of friends this user has
}

interface NetworkLink extends d3Force.SimulationLinkDatum<NetworkNode> {
    source: NetworkNode | SteamID64;
    target: NetworkNode | SteamID64;
}

interface NetworkGraphProps {
    mainProfile: ProfileSummary;
    friends: FriendData[];
    friendsOfFriends: Map<SteamID64, FriendData[]>;
    friendCounts: Map<SteamID64, number>; // Map of steamId to total friend count
    onClose: () => void;
}

function NetworkGraph({mainProfile, friends, friendsOfFriends, friendCounts, onClose}: NetworkGraphProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const simulationRef = useRef<d3Force.Simulation<NetworkNode, NetworkLink> | null>(null);
    const gRef = useRef<d3Selection.Selection<SVGGElement, unknown, null, undefined> | null>(null);
    const nodesMapRef = useRef<Map<SteamID64, NetworkNode>>(new Map());
    const [dimensions, setDimensions] = useState({width: 800, height: 600});
    const [tooltip, setTooltip] = useState<{visible: boolean; x: number; y: number; content: string}>({
        visible: false,
        x: 0,
        y: 0,
        content: '',
    });

    useEffect(() => {
        const updateDimensions = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            setDimensions({width, height});
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Cleanup simulation on unmount
    useEffect(() => {
        return () => {
            if (simulationRef.current) {
                simulationRef.current.stop();
            }
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current) return;

        const {width, height} = dimensions;
        const svg = d3Selection.select(svgRef.current);

        // Initialize SVG structure only once
        if (!gRef.current) {
            svg.selectAll('*').remove();
            const g = svg.append('g');
            gRef.current = g;

            // Create zoom behavior
            const zoom = d3Zoom.zoom<SVGSVGElement, unknown>()
                .scaleExtent([0.1, 4])
                .on('zoom', (event) => {
                    g.attr('transform', event.transform);
                });
            svg.call(zoom);

            // Create link and node groups
            g.append('g').attr('class', 'links-layer');
            g.append('g').attr('class', 'nodes-layer');
        }

        const g = gRef.current;

        // Create nodes and links
        const nodes: NetworkNode[] = [];
        const links: NetworkLink[] = [];
        const nodeMap = new Map<SteamID64, NetworkNode>();

        // Calculate friend ban counts
        const calculateFriendBanCount = (steamId: SteamID64): number => {
            const friendsList = friendsOfFriends.get(steamId) || [];
            return friendsList.filter(f => f.bans.VACBanned || f.bans.NumberOfGameBans > 0).length;
        };

        // Helper function to create or update a node, preserving position if it exists
        const createOrUpdateNode = (id: SteamID64, data: Partial<NetworkNode>): NetworkNode => {
            const existingNode = nodesMapRef.current.get(id);
            const node: NetworkNode = {
                id,
                name: data.name || '',
                vacBanned: data.vacBanned || false,
                gameBans: data.gameBans || 0,
                friendBanCount: data.friendBanCount || 0,
                isCenter: data.isCenter || false,
                depth: data.depth || 0,
                mutualFriendsCount: data.mutualFriendsCount || 0,
                totalFriendCount: data.totalFriendCount || 0,
                // Preserve existing position if node already exists
                x: existingNode?.x,
                y: existingNode?.y,
                vx: existingNode?.vx,
                vy: existingNode?.vy,
                fx: existingNode?.fx,
                fy: existingNode?.fy,
            };

            // For center node, always fix position
            if (data.isCenter) {
                node.x = width / 2;
                node.y = height / 2;
                node.fx = width / 2;
                node.fy = height / 2;
            }

            return node;
        };

        // Add center node (main profile)
        const centerNode = createOrUpdateNode(mainProfile.id64, {
            name: mainProfile.persona,
            vacBanned: mainProfile.vacBanned,
            gameBans: mainProfile.gameBans,
            friendBanCount: friends.filter(f => f.bans.VACBanned || f.bans.NumberOfGameBans > 0).length,
            isCenter: true,
            depth: 0,
            mutualFriendsCount: 0,
            totalFriendCount: friendCounts.get(mainProfile.id64) || friends.length,
        });
        nodes.push(centerNode);
        nodeMap.set(mainProfile.id64, centerNode);

        // Add first-level friends (depth 1)
        friends.forEach(friend => {
            const node = createOrUpdateNode(friend.player.steamid, {
                name: friend.player.personaname,
                vacBanned: friend.bans.VACBanned,
                gameBans: friend.bans.NumberOfGameBans,
                friendBanCount: calculateFriendBanCount(friend.player.steamid),
                isCenter: false,
                depth: 1,
                mutualFriendsCount: 0,
                totalFriendCount: friendCounts.get(friend.player.steamid) || 0,
            });
            nodes.push(node);
            nodeMap.set(friend.player.steamid, node);

            // Link to center
            links.push({
                source: centerNode,
                target: node,
            });
        });

        // Add second-level friends (friends of friends, depth 2)
        const addedFoF = new Set<SteamID64>();
        friendsOfFriends.forEach((fofList, parentId) => {
            fofList.forEach(fof => {
                if (!nodeMap.has(fof.player.steamid) && !addedFoF.has(fof.player.steamid)) {
                    const node = createOrUpdateNode(fof.player.steamid, {
                        name: fof.player.personaname,
                        vacBanned: fof.bans.VACBanned,
                        gameBans: fof.bans.NumberOfGameBans,
                        friendBanCount: 0,
                        isCenter: false,
                        depth: 2,
                        mutualFriendsCount: 0,
                        totalFriendCount: friendCounts.get(fof.player.steamid) || 0,
                    });
                    nodes.push(node);
                    nodeMap.set(fof.player.steamid, node);
                    addedFoF.add(fof.player.steamid);
                }

                // Link to parent friend
                const parentNode = nodeMap.get(parentId);
                const targetNode = nodeMap.get(fof.player.steamid);
                if (parentNode && targetNode) {
                    links.push({
                        source: parentNode,
                        target: targetNode,
                    });
                }
            });
        });

        // Calculate mutual friends count for all nodes (how many friends connect to root)
        nodes.forEach(node => {
            if (node.depth === 2) {
                // Count how many depth-1 nodes connect to this depth-2 node
                let count = 0;
                links.forEach(link => {
                    const source = link.source as NetworkNode;
                    const target = link.target as NetworkNode;
                    if ((target.id === node.id && source.depth === 1) ||
                        (source.id === node.id && target.depth === 1)) {
                        count++;
                    }
                });
                node.mutualFriendsCount = count;
            }
        });

        // Filter out pure leaf FoF nodes if toggle is disabled
        // A pure leaf FoF is a depth-2 node with only 1 connection (unless connected to root or has bans)
        let filteredNodes = nodes;
        let filteredLinks = links;
        if (!RENDER_LEAF_FOF_NODES) {
            const leafFoFIds = new Set<SteamID64>();
            
            // Identify leaf FoF nodes
            nodes.forEach(node => {
                if (node.depth === 2) {
                    // Count connections to this node
                    let connectionCount = 0;
                    let connectedToRoot = false;
                    
                    links.forEach(link => {
                        const source = link.source as NetworkNode;
                        const target = link.target as NetworkNode;
                        if (target.id === node.id || source.id === node.id) {
                            connectionCount++;
                            if (source.isCenter || target.isCenter) {
                                connectedToRoot = true;
                            }
                        }
                    });
                    
                    // Mark as leaf if only 1 connection, not connected to root, AND no bans
                    const hasBans = node.vacBanned || node.gameBans > 0;
                    if (/*connectionCount === 1 &&*/ !connectedToRoot && !hasBans) {
                        leafFoFIds.add(node.id);
                    }
                }
            });
            
            // Filter out leaf FoF nodes
            filteredNodes = nodes.filter(node => !leafFoFIds.has(node.id));
            filteredLinks = links.filter(link => {
                const source = link.source as NetworkNode;
                const target = link.target as NetworkNode;
                return !leafFoFIds.has(source.id) && !leafFoFIds.has(target.id);
            });
        }

        // Initialize positions for better circular layout
        // Give depth-1 nodes evenly distributed angular positions
        const depth1Nodes = filteredNodes.filter(n => n.depth === 1);
        depth1Nodes.forEach((node, index) => {
            if (!node.x || !node.y) {
                const angle = (index / depth1Nodes.length) * 2 * Math.PI;
                const radius = 200;
                node.x = width / 2 + radius * Math.cos(angle);
                node.y = height / 2 + radius * Math.sin(angle);
            }
        });

        // Update links using data join
        const link = g.select('.links-layer')
            .selectAll<SVGLineElement, NetworkLink>('line')
            .data(filteredLinks, (d: NetworkLink) => {
                const source = d.source as NetworkNode;
                const target = d.target as NetworkNode;
                return `${source.id}-${target.id}`;
            })
            .join(
                enter => enter.append('line')
                    .attr('class', 'network-link')
                    .attr('stroke', '#999')
                    .attr('stroke-opacity', 0.3)
                    .attr('stroke-width', 1),
                update => update,
                exit => exit.remove()
            );

        // Sort nodes by priority (lower priority = render first = below)
        const sortedNodes = [...filteredNodes].sort((a, b) => {
            if (a.depth !== b.depth) return b.depth - a.depth;
            const aBanned = a.vacBanned || a.gameBans > 0;
            const bBanned = b.vacBanned || b.gameBans > 0;
            if (aBanned !== bBanned) return aBanned ? 1 : -1;
            return a.friendBanCount - b.friendBanCount;
        });
        const opacityMult = (d: number) => Math.log10(-d / 2.5 + 1) + 1;
        
        // Helper function to calculate node size based on friend count
        const calculateNodeSize = (d: NetworkNode): number => {
            // Size based entirely on friend count - sqrt to be proportional to area
            const baseSize = 5;
            const friendCountBoost = d.totalFriendCount > 0 
                ? Math.sqrt(d.totalFriendCount)
                : 0;
            
            return (baseSize + friendCountBoost) * 2;
        };
        
        // Update nodes using data join
        const node = g.select('.nodes-layer')
            .selectAll<SVGCircleElement, NetworkNode>('circle')
            .data(sortedNodes, (d: NetworkNode) => d.id)
            .join(
                enter => enter.append('circle')
                    .attr('class', 'network-node')
                    .style('cursor', 'pointer')
                    .call(enter => enter
                        .attr('r', 0) // Start with 0 radius for animation
                        .transition()
                        .duration(TRANSITION_DURATION)
                        .attr('r', calculateNodeSize)
                    ),
                update => update
                    .transition()
                    .duration(TRANSITION_DURATION)
                    .attr('r', calculateNodeSize),
                exit => exit
                    .transition()
                    .duration(TRANSITION_DURATION)
                    .attr('r', 0)
                    .remove()
            )
            .attr('fill', d => {
                if (d.isCenter) return '#fff';
                if (d.vacBanned) return 'rgba(244, 63, 94, 0.8)';
                if (d.gameBans > 0) return 'rgba(245, 158, 11, 0.8)';
                return `rgba(16, 185, 129, ${opacityMult(d.depth)})`;
            })
            .attr('fill-opacity', d => {
                if (d.isCenter) return 1.0;
                if (d.vacBanned || d.gameBans > 0) return 1.0;
                return 1.0;
            })
            .attr('stroke', d => {
                if (d.vacBanned) return '#fda4af';
                if (d.gameBans > 0) return '#fcd34d';
                if (d.isCenter) return '#6ee7b7';
                return `rgba(110, 231, 183, ${opacityMult(d.depth)})`;
            })
            .attr('stroke-width', d => {
                if (d.isCenter) return 3;
                if (d.vacBanned || d.gameBans > 0) return 2;
                return 1.5;
            })
        // Set up event handlers on nodes
        node
            .on('mouseover', function(event, d) {
                const banStatus = d.vacBanned ? 'VAC Banned' : d.gameBans > 0 ? `${d.gameBans} Game Ban(s)` : 'No Bans';
                const friendInfo = d.friendBanCount > 0 ? `${d.friendBanCount} friends with bans` : 'No banned friends';
                const depthLabel = d.depth === 0 ? 'Root' : d.depth === 1 ? 'Friend' : 'Friend of Friend';
                const totalFriendsInfo = `Total Friends: ${d.totalFriendCount}`;
                const mutualInfo = `Mutual Friends With Root: ${d.mutualFriendsCount}`;
                setTooltip({
                    visible: true,
                    x: event.pageX,
                    y: event.pageY,
                    content: `${d.name}\n${banStatus}\n${friendInfo}\n${totalFriendsInfo}\n${mutualInfo}\nDepth: ${depthLabel}`,
                });
                d3Selection.select(this).attr('stroke-width', d.isCenter ? 4 : 3);

                link.attr('stroke-opacity', (l: NetworkLink) => {
                    const source = l.source as NetworkNode;
                    const target = l.target as NetworkNode;
                    return (source.id === d.id || target.id === d.id) ? 0.8 : 0.1;
                })
                .attr('stroke-width', (l: NetworkLink) => {
                    const source = l.source as NetworkNode;
                    const target = l.target as NetworkNode;
                    return (source.id === d.id || target.id === d.id) ? 2 : 1;
                });
            })
            .on('mouseout', function(_, d) {
                setTooltip({visible: false, x: 0, y: 0, content: ''});
                d3Selection.select(this).attr('stroke-width', d.isCenter ? 3 : (d.vacBanned || d.gameBans > 0) ? 2 : 1.5);

                link.attr('stroke-opacity', 0.3)
                    .attr('stroke-width', 1);
            })
            .on('mousemove', (event) => {
                setTooltip(prev => ({...prev, x: event.pageX, y: event.pageY}));
            });

        // Update or create simulation
        if (!simulationRef.current) {
            simulationRef.current = d3Force.forceSimulation(filteredNodes)
                .force('link', d3Force.forceLink<NetworkNode, NetworkLink>(filteredLinks)
                    .id(d => d.id)
                    .distance(distCalc)
                )
                .force('charge', d3Force.forceManyBody().strength(-150))
                .force('center', d3Force.forceCenter(width / 2, height / 2))
                .force('collision', d3Force.forceCollide<NetworkNode>().radius((d: NetworkNode) => {
                    return calculateNodeSize(d) + 2;
                }))
                .force('radial', d3Force.forceRadial<NetworkNode>(
                    forceCalc,
                    width / 2,
                    height / 2
                ).strength(d => d.depth === 2 ? 0.3 : 0.6));
        } else {
            // Update existing simulation with new data
            const simulation = simulationRef.current;
            simulation.nodes(filteredNodes);
            simulation.force('link', d3Force.forceLink<NetworkNode, NetworkLink>(filteredLinks)
                .id(d => d.id)
                .distance(distCalc)
            );
            // Update radial force with stronger strength for depth-1
            simulation.force('radial', d3Force.forceRadial<NetworkNode>(
                forceCalc,
                width / 2,
                height / 2
            ).strength(d => d.depth === 2 ? 0.3 : 0.6));
            simulation.alpha(0.3).restart();
        }

        const simulation = simulationRef.current;

        // Update positions on each tick
        simulation.on('tick', () => {
            link
                .attr('x1', d => (d.source as NetworkNode).x!)
                .attr('y1', d => (d.source as NetworkNode).y!)
                .attr('x2', d => (d.target as NetworkNode).x!)
                .attr('y2', d => (d.target as NetworkNode).y!);

            node
                .attr('cx', d => d.x!)
                .attr('cy', d => d.y!);
        });

        // Add drag behavior (except for center node)
        const drag = d3Drag.drag<SVGCircleElement, NetworkNode>()
            .on('start', (event, d) => {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                if (!d.isCenter) {
                    d.fx = d.x;
                    d.fy = d.y;
                }
            })
            .on('drag', (event, d) => {
                if (!d.isCenter) {
                    d.fx = event.x;
                    d.fy = event.y;
                }
            })
            .on('end', (event, d) => {
                if (!event.active) simulation.alphaTarget(0);
                if (!d.isCenter) {
                    d.fx = null;
                    d.fy = null;
                }
            });

        node.call(drag as any);

        // Store current nodes in ref for next render to preserve positions
        nodesMapRef.current = nodeMap;

        return () => {
            // Don't stop simulation on cleanup, let it keep running
        };
    }, [mainProfile, friends, friendsOfFriends, friendCounts, dimensions, setTooltip]);

    return (
        <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-950">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Friends Network</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {mainProfile.persona}'s network (depth 2)
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="icon-button text-gray-900 dark:text-gray-100"
                    aria-label="Close"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>

            {/* Legend - styled like profile section card */}
            <div className="absolute bottom-4 left-4 z-10 card p-3 text-sm">
                <div className="font-semibold mb-2">Legend</div>
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-rose-500 ring-2 ring-rose-300" style={{opacity: 0.8}}></div>
                        <span className="text-gray-700 dark:text-gray-300">VAC Banned</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500 ring-2 ring-amber-300" style={{opacity: 0.8}}></div>
                        <span className="text-gray-700 dark:text-gray-300">Game Banned</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-emerald-300" style={{opacity: 0.15}}></div>
                        <span className="text-gray-700 dark:text-gray-300">No Bans</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-1.5 border-t border-gray-200 dark:border-gray-700">
                        Size = total friend count
                    </div>
                </div>
            </div>

            {/* Tooltip */}
            {tooltip.visible && (
                <div
                    ref={tooltipRef}
                    className="fixed z-50 pointer-events-none card p-2 text-sm whitespace-pre-line"
                    style={{
                        left: tooltip.x + 10,
                        top: tooltip.y + 10,
                    }}
                >
                    {tooltip.content}
                </div>
            )}

            {/* Graph */}
            <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                className="absolute inset-0"
            />
        </div>
    );
}

export default NetworkGraph;
